import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    IconButton,
    TextField,
    Checkbox,
    Alert,
    InputAdornment,
    Tooltip,
} from '@mui/material';
import {
    Close as CloseIcon, Search as SearchIcon, DeleteOutline as DeleteOutlineIcon,
    KeyboardArrowDown, KeyboardArrowUp, KeyboardArrowLeft, KeyboardArrowRight, ListAltOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DocumentService from '../../../Services/documents.service.js';
import Constants from '../../../assests/Constants/constants.js';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import LinkDocumentItemsPanel from './LinkDocumentItemsPanel.jsx';
import LinkDocumentMatchChip from './LinkDocumentMatchChip.jsx';
import {
    getStatusLabelKey, getStatusClassName,
    RELATION_TYPE,
    LINK_DIRECTION,
    ITEM_MATCH_STATUS,
    compareItemRows,
    anchorKey,
    anchorRelationLabelKey,
    buildLinkManyRequest,
    buildPendingLinks,
    canSelectMore,
    comparePaymentSelectionItems,
    defaultSelectionAmount,
    exceedsPaymentAnchor,
    remainingForSelections,
    filterLinkCandidates,
    findGroupReceiptMatches,
    findLinkSelections,
    formatLinkAmount,
    formatLinkDate,
    isLinkCandidateBlocked,
    isPaymentCandidateTooLarge,
    linkStatusCode,
    normalizeLinkOptions,
    parseLinkError,
    pendingLinksToSelections,
    projectLink,
    projectLinkSelections,
    roundAmount,
    selectionsFromExactMatches,
    validateLinkSelections,
} from '../documentLifecycle.js';

const typeLabel = (documentType) => Constants.TransactionDocuments?.[documentType] ?? '';

const statusChip = (t, doc) => {
    const row = { processingStatus: doc?.processingStatus, transactionDocumentType: doc?.documentType };
    const className = getStatusClassName(row);
    const color = className === 'processed' ? 'success' : className === 'partially-processed' ? 'warning' : 'error';
    return <Chip size="small" variant="outlined" color={color} label={t(getStatusLabelKey(row))} />;
};

const comparisonResultKey = (result) => ({
    match: 'LinkDocumentsItemRowMatch',
    qtyDiff: 'LinkDocumentsItemRowQtyDiff',
    notIncluded: 'LinkDocumentsItemRowNotIncluded',
    different: 'LinkDocumentsItemRowDifferent',
    mismatch: 'LinkDocumentsItemRowMismatch',
}[result] || 'LinkDocumentsItemRowMismatch');

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 1, color: '#304FFF' }}>
        {children}
    </Typography>
);

/** Same thin scrollbar as AutocompleteSelectFilter's list. */
const scrollbarSx = {
    scrollbarWidth: 'thin',
    scrollbarColor: '#d8daf2 transparent',
    '&::-webkit-scrollbar': { width: 6, height: 6 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: '#d8daf2', borderRadius: 999 },
};

/** Soft pill for "show / hide items"; filled a little more while the items are open. */
const itemsToggleSx = (open) => ({
    textTransform: 'none',
    borderRadius: 999,
    px: 1.75,
    py: 0.375,
    minHeight: 32,
    gap: 0.25,
    fontSize: 13,
    fontWeight: 600,
    color: '#304FFF',
    borderColor: open ? '#304FFF' : '#d6dcff',
    backgroundColor: open ? '#e8ecff' : '#f5f7ff',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    '& .MuiButton-startIcon, & .MuiButton-endIcon': { mx: 0.5 },
    '&:hover': { backgroundColor: '#e8ecff', borderColor: '#304FFF' },
});

/** Same frame as AutocompleteSelectFilter's trigger. */
const amountInputSx = {
    height: 40,
    px: 1.5,
    border: '2px solid #e5e7f6',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    fontSize: 14,
    fontWeight: 500,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': { borderColor: '#3366ff' },
    '&.Mui-focused': { borderColor: '#3366ff', boxShadow: '0 0 0 3px rgba(48, 79, 255, 0.14)' },
    '&.Mui-error': { borderColor: '#d32f2f' },
};

// Receipt ↔ invoices items are compared only between untouched documents: a partly paid one no
// longer stands for all its lines.
const isWholeAmount = (amount, available) => Math.abs(roundAmount(amount) - roundAmount(available)) <= 0.01;
const isWholeDocument = (doc) => isWholeAmount(doc?.amount, doc?.availableAmount);

const Info = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
    </Box>
);

/**
 * Two modes:
 *  - "existing" (default): link a document that already has an id, saved through the link API
 *    (used from the TransactionDocuments grid).
 *  - "create": pick the existing documents a document being created will be linked to. Nothing is
 *    saved; the chosen links are returned through `onConfirm(pendingLinks)` and persisted by the
 *    creation form when the document is generated. `draft` carries the new document's type / customer
 *    / amount; `pendingLinks` pre-selects the dialog.
 */
const LinkDocumentsModal = ({
    open, onClose, caseId, token, documentId: documentIdProp, pairDocumentIds, direction, onLinked,
    mode = 'existing', draft = null, pendingLinks = null, onConfirm = null, invoiceGroupIds = null,
}) => {
    const { t } = useTranslation();
    const isCreate = mode === 'create';
    // Several TaxInvoices selected: first pick the one receipt that matches them all, then link it.
    const invoiceGroupKey = (invoiceGroupIds || []).filter(Boolean).map(String).join(',');
    const [groupReceiptId, setGroupReceiptId] = useState(null);
    const [group, setGroup] = useState(null);
    const [groupSkipped, setGroupSkipped] = useState(false);
    const groupMode = !isCreate && invoiceGroupKey.includes(',') && !groupReceiptId && !groupSkipped;
    const documentId = groupReceiptId || documentIdProp;
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [options, setOptions] = useState(null);
    const [selectedAnchorKey, setSelectedAnchorKey] = useState('');
    // { [candidateId]: amount as typed }
    const [selections, setSelections] = useState({});
    const [search, setSearch] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [pairNotLinkable, setPairNotLinkable] = useState(false);
    const [linkToRemove, setLinkToRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [itemCache, setItemCache] = useState({});

    // A string, so a new array with the same ids from the parent does not reload the dialog.
    const pairKey = groupReceiptId
        ? invoiceGroupKey
        : groupSkipped ? '' : (pairDocumentIds || []).filter(Boolean).map(String).join(',');
    const draftItemsKey = isCreate
        ? (draft?.items || []).map((i) => `${i.itemId || ''}:${i.makat || ''}:${i.name || ''}:${i.quantity}:${i.price}:${i.discount}:${i.includeVat}`).join(',')
        : '';
    const draftKey = isCreate && draft
        ? [draft.entityType, draft.documentType, draft.customerAccountCodeId, draft.amount, draft.currency, draftItemsKey].join('|')
        : '';
    // Pending links identity: re-opening after confirm must re-check the already chosen rows.
    const pendingLinksKey = isCreate
        ? (pendingLinks || []).map((l) => `${l.documentId}:${l.appliedAmount}`).join(',')
        : '';
    const pendingLinksRef = useRef(pendingLinks);
    pendingLinksRef.current = pendingLinks;
    const itemCacheRef = useRef(itemCache);
    itemCacheRef.current = itemCache;

    const reset = () => {
        setOptions(null);
        setSelectedAnchorKey('');
        setSelections({});
        setSearch('');
        setSubmitError('');
        setLoadError('');
        setPairNotLinkable(false);
        setExpanded({});
        setItemCache({});
    };

    /** Picks the anchor that already holds the pending links, so re-opening keeps them selected. */
    const presetFromPendingLinks = (normalized) => {
        const links = pendingLinksRef.current || [];
        const wanted = links.map((l) => String(l.documentId));
        const anchorsList = normalized?.anchors || [];
        const chosen = wanted.length
            ? anchorsList.find((a) => a.candidates.some((c) => wanted.includes(String(c.documentId))))
            : null;
        const target = chosen || anchorsList[0];
        setSelectedAnchorKey(target ? anchorKey(target) : '');
        if (wanted.length && target) {
            const ids = new Set(target.candidates
                .filter((c) => !exceedsPaymentAnchor(target, c))
                .map((c) => String(c.documentId)));
            const kept = links.filter((l) => ids.has(String(l.documentId))
                && !isLinkCandidateBlocked(l, target.relationType));
            setSelections(pendingLinksToSelections(kept));
        } else {
            setSelections(selectionsFromExactMatches(target));
        }
    };

    const loadOptions = useCallback(async () => {
        if (!caseId || !token || groupMode) return;
        if (isCreate) {
            if (!draft || !(Number(draft.amount) > 0) || !draft.customerAccountCodeId) {
                setOptions(null);
                return;
            }
        } else if (!documentId) {
            return;
        }
        const pairIds = pairKey ? pairKey.split(',') : [];
        setLoading(true);
        setLoadError('');
        try {
            const resp = isCreate
                ? await DocumentService.GetDraftDocumentLinkOptions(caseId, draft, token)
                : await DocumentService.GetDocumentLinkOptions(
                    caseId, documentId, token, pairIds.length === 1 ? pairIds[0] : null);
            if (!resp.ok) {
                const { message } = parseLinkError(await resp.text());
                throw new Error(message || t('LinkDocumentsLoadFailed'));
            }
            const normalized = normalizeLinkOptions(await resp.json());
            setOptions(normalized);

            if (isCreate) {
                presetFromPendingLinks(normalized);
                return;
            }

            const preset = findLinkSelections(normalized, documentId, pairIds);
            if (preset) {
                setSelectedAnchorKey(anchorKey(preset.anchor));
                setSelections(preset.selections);
                setPairNotLinkable(preset.missing > 0);
            } else {
                setPairNotLinkable(pairIds.length > 0);
                const first = normalized?.anchors?.[0];
                setSelectedAnchorKey(first ? anchorKey(first) : '');
            }
        } catch (err) {
            console.error('Error loading link options:', err);
            setLoadError(err?.message || t('LinkDocumentsLoadFailed'));
        } finally {
            setLoading(false);
        }
        // pendingLinksKey refreshes the create-mode preset when the user re-opens after confirming.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId, documentId, pairKey, draftKey, pendingLinksKey, isCreate, groupMode, token, t]);

    useEffect(() => {
        if (open) {
            reset();
            setLinkToRemove(null);
            loadOptions();
        } else {
            setGroupReceiptId(null);
            setGroupSkipped(false);
            setGroup(null);
        }
    }, [open, loadOptions]);

    useEffect(() => {
        if (!open || !groupMode || !caseId || !token) return undefined;
        let cancelled = false;
        setGroup({ loading: true });
        (async () => {
            try {
                const list = await Promise.all(invoiceGroupKey.split(',').map(async (id) => {
                    const resp = await DocumentService.GetDocumentLinkOptions(caseId, id, token, null);
                    if (!resp.ok) {
                        const { message } = parseLinkError(await resp.text());
                        throw new Error(message || t('LinkDocumentsLoadFailed'));
                    }
                    return normalizeLinkOptions(await resp.json());
                }));
                if (cancelled) return;
                const matches = findGroupReceiptMatches(list);
                setGroup({ loading: false, ...matches });
                if (matches.receipts.length && matches.invoices.every(isWholeDocument)) {
                    [...matches.invoices, ...matches.receipts.filter(isWholeDocument)]
                        .forEach((d) => ensureItems(d.documentId));
                }
            } catch (err) {
                console.error('Error loading group receipt options:', err);
                if (!cancelled) setGroup({ loading: false, error: err?.message || t('LinkDocumentsLoadFailed') });
            }
        })();
        return () => { cancelled = true; };
        // ensureItems only fills the item cache.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, groupMode, invoiceGroupKey, caseId, token, t]);

    const handleRemoveLink = async () => {
        if (!linkToRemove) return;
        setRemoving(true);
        setSubmitError('');
        try {
            const resp = await DocumentService.RemoveDocumentLink(caseId, linkToRemove.relationshipId, token);
            if (!resp.ok) {
                const { message } = parseLinkError(await resp.text());
                setSubmitError(message || t('LinkDocumentsRemoveFailed'));
                return;
            }
            toast.success(t('LinkDocumentsRemoveSuccess'));
            setLinkToRemove(null);
            reset();
            await loadOptions();
            if (onLinked) await onLinked();
        } catch (err) {
            console.error('Error removing document link:', err);
            setSubmitError(err?.message || t('LinkDocumentsRemoveFailed'));
        } finally {
            setRemoving(false);
            setLinkToRemove(null);
        }
    };

    const anchors = useMemo(() => options?.anchors || [], [options]);
    const anchor = useMemo(
        () => anchors.find((a) => anchorKey(a) === selectedAnchorKey) || null,
        [anchors, selectedAnchorKey]
    );
    const documentsById = useMemo(() => {
        const map = new Map();
        (options?.chainDocuments || []).forEach((d) => map.set(String(d.documentId), d));
        if (options?.document) map.set(String(options.document.documentId), options.document);
        return map;
    }, [options]);
    const anchorDocument = anchor ? documentsById.get(String(anchor.documentId)) : null;

    const isPaymentLink = Number(anchor?.relationType) === RELATION_TYPE.PAYMENT_FOR;
    // A payment candidate bigger than the anchor could only be linked in part, which never matches.
    const candidates = useMemo(
        () => (anchor?.candidates || []).filter((c) => !exceedsPaymentAnchor(anchor, c)),
        [anchor]
    );
    const hiddenLargerCount = (anchor?.candidates?.length || 0) - candidates.length;
    const visibleCandidates = useMemo(
        () => filterLinkCandidates(candidates, search, typeLabel),
        [candidates, search]
    );
    const selectedCandidates = useMemo(
        () => candidates.filter((c) => Object.prototype.hasOwnProperty.call(selections, String(c.documentId))),
        [candidates, selections]
    );

    const validation = validateLinkSelections(anchor, selections, candidates);
    const projection = validation.isValid
        ? projectLinkSelections(
            { ...anchor, documentType: anchorDocument?.documentType ?? anchor?.documentType },
            selections,
            candidates,
        )
        : null;
    const showRowStatus = anchor?.direction === LINK_DIRECTION.AS_TARGET
        || Number(anchor?.relationType) === RELATION_TYPE.PAYMENT_FOR;
    // In create mode nothing is saved, so confirming an empty selection is allowed (it clears the
    // pending links); only row errors or exceeding the anchor block the confirm.
    const hasBlockingErrors = Object.keys(validation.rowErrors).length > 0
        || (validation.totalError && validation.totalError !== 'LinkDocumentsSelectCandidate');
    // Terminal docs (InvoiceReceipt / Receipt) often show Closed even with open allocation.
    // Hide new-candidate UI only when the server returned no anchors left to extend.
    const flowClosed = !isCreate && anchors.length === 0;
    const showCandidates = isCreate || anchors.length > 0;
    const remainingToLink = remainingForSelections(anchor, selections);
    const selectionFull = !!anchor && !canSelectMore(anchor, selections);

    const handleAnchorChange = (key) => {
        setSelectedAnchorKey(key);
        const next = anchors.find((a) => anchorKey(a) === key) || null;
        setSelections(isCreate ? selectionsFromExactMatches(next) : {});
        setSubmitError('');
    };

    const cacheKey = (id) => (id ? String(id) : 'draft');

    const linesOf = (id) => {
        if (!id || String(id) === 'draft' || (isCreate && String(id) === String(options?.document?.documentId || ''))) {
            return draft?.items || [];
        }
        return itemCache[String(id)]?.items || [];
    };

    const ensureItems = async (id) => {
        if (!id || String(id) === 'draft' || String(id) === '00000000-0000-0000-0000-000000000000') return;
        const key = String(id);
        if (itemCacheRef.current[key]?.loading || itemCacheRef.current[key]?.loaded) return;
        itemCacheRef.current = { ...itemCacheRef.current, [key]: { loading: true, loaded: false, items: [], error: '' } };
        setItemCache(itemCacheRef.current);
        try {
            const items = await DocumentService.GetDocumentItems(caseId, id, token);
            const next = { loading: false, loaded: true, items: Array.isArray(items) ? items : [], error: '' };
            itemCacheRef.current = { ...itemCacheRef.current, [key]: next };
            setItemCache(itemCacheRef.current);
        } catch (err) {
            const next = { loading: false, loaded: true, items: [], error: err?.message || t('LinkDocumentsItemsLoadFailed') };
            itemCacheRef.current = { ...itemCacheRef.current, [key]: next };
            setItemCache(itemCacheRef.current);
        }
    };

    const paymentItemDocs = isPaymentLink && selectedCandidates.length > 0
        && isWholeAmount(isCreate ? draft?.amount : anchorDocument?.amount, anchor.availableAmount)
        && selectedCandidates.every((c) => isWholeAmount(c.amount, c.availableAmount))
        ? { anchorId: isCreate ? 'draft' : String(anchor.documentId), ids: selectedCandidates.map((c) => String(c.documentId)) }
        : null;
    const paymentItemDocsKey = paymentItemDocs ? [paymentItemDocs.anchorId, ...paymentItemDocs.ids].join(',') : '';

    useEffect(() => {
        if (!paymentItemDocsKey) return;
        paymentItemDocsKey.split(',').forEach((id) => ensureItems(id));
        // ensureItems skips documents already loading / loaded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentItemDocsKey]);

    const paymentItemsStatus = (() => {
        if (!paymentItemDocs) return null;
        const pending = [paymentItemDocs.anchorId, ...paymentItemDocs.ids]
            .some((id) => id !== 'draft' && !itemCache[id]?.loaded);
        if (pending) return 'loading';
        return comparePaymentSelectionItems(linesOf(paymentItemDocs.anchorId), paymentItemDocs.ids.map(linesOf));
    })();
    const paymentItemsBlock = paymentItemsStatus === 'loading' || paymentItemsStatus === 'mismatch';
    const canSubmit = !submitting && !flowClosed && !paymentItemsBlock
        && (isCreate ? !hasBlockingErrors : validation.isValid);

    // Group step: receipts equal to the invoices' total, judged on items when all are untouched.
    const groupRows = (groupMode && group?.receipts ? group.receipts : []).map((c) => {
        if (!group.invoices.every(isWholeDocument) || !isWholeDocument(c)) return { candidate: c, items: null };
        const ids = [c.documentId, ...group.invoices.map((inv) => inv.documentId)].map(String);
        if (ids.some((id) => !itemCache[id]?.loaded)) return { candidate: c, items: 'loading' };
        return {
            candidate: c,
            items: comparePaymentSelectionItems(linesOf(c.documentId), group.invoices.map((inv) => linesOf(inv.documentId))),
        };
    });
    const visibleGroupRows = groupRows.filter((row) => row.items !== 'mismatch');
    const hiddenGroupCount = (group?.otherCount || 0) + (groupRows.length - visibleGroupRows.length);
    // The invoice the dialog was opened on, to link it alone instead.
    const groupOwnInvoice = groupMode && group && !group.loading && !group.error
        ? [...(group.invoices || []), ...(group.closed || [])]
            .find((inv) => String(inv.documentId) === String(documentIdProp)) || null
        : null;

    const peerDocumentId = isCreate ? 'draft' : (anchor?.documentId || options?.document?.documentId);

    const toggleExpand = (event, id) => {
        event.stopPropagation();
        const key = cacheKey(id);
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
        ensureItems(id);
        if (peerDocumentId && String(peerDocumentId) !== String(id)) ensureItems(peerDocumentId);
    };

    const toggleCandidate = (c) => {
        const id = String(c.documentId);
        const selected = Object.prototype.hasOwnProperty.call(selections, id);
        if (!selected && isLinkCandidateBlocked(c, anchor?.relationType)) return;
        if (selected) {
            const { [id]: _removed, ...rest } = selections;
            setSelections(rest);
        } else {
            // The selected amounts may never exceed what the anchor has available.
            if (!canSelectMore(anchor, selections)) {
                toast.info(t('LinkDocumentsSelectionFull', { amount: formatLinkAmount(anchor?.availableAmount) }));
                return;
            }
            if (isPaymentCandidateTooLarge(anchor, c, selections)) {
                toast.info(t('LinkDocumentsPaymentTooLarge', { amount: formatLinkAmount(remainingForSelections(anchor, selections)) }));
                return;
            }
            setSelections({ ...selections, [id]: defaultSelectionAmount(anchor, c, selections) });
        }
        setSubmitError('');
    };

    const setSelectionAmount = (id, value) => {
        setSelections((prev) => ({ ...prev, [String(id)]: value }));
        setSubmitError('');
    };

    const handleSubmit = async () => {
        if (isCreate) {
            const candidatesWithItems = candidates.map((c) => ({
                ...c,
                items: itemCache[String(c.documentId)]?.items || c.items || [],
            }));
            if (onConfirm) onConfirm(buildPendingLinks(anchor, selections, candidatesWithItems));
            onClose();
            return;
        }
        const request = buildLinkManyRequest(caseId, anchor, selections, candidates);
        if (!request.links.length) return;
        setSubmitting(true);
        setSubmitError('');
        try {
            const resp = await DocumentService.LinkManyDocuments(request, token);
            if (!resp.ok) {
                const { code, message } = parseLinkError(await resp.text());
                setSubmitError(translateLinkCode(code) || message || t('LinkDocumentsFailed'));
                return;
            }
            toast.success(t('LinkDocumentsSuccess'));
            onClose();
            if (onLinked) await onLinked();
        } catch (err) {
            console.error('Error linking documents:', err);
            setSubmitError(err?.message || t('LinkDocumentsFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    const translateLinkCode = (code) => {
        const key = code ? `LinkDocumentsError_${code}` : null;
        const translated = key ? t(key) : '';
        return translated && translated !== key ? translated : '';
    };
    const pairReason = translateLinkCode(linkStatusCode(options?.pairStatus));

    const doc = options?.document;
    const describe = (d) => (d ? `${typeLabel(d.documentType)} ${d.reference || ''}`.trim() : '');
    const anchorOptions = anchors.map((a) => ({
        value: anchorKey(a),
        label: `${describe(documentsById.get(String(a.documentId)))} — ${t(anchorRelationLabelKey(a))}`,
    }));
    const inputAlignClass = direction === 'ltr' ? 'ltr-input' : 'rtl-input';

    return (
        <Dialog
            open={open}
            onClose={submitting || removing ? undefined : onClose}
            maxWidth="lg"
            fullWidth
            dir={direction}
            PaperProps={{ dir: direction }}
        >
            <DialogTitle
                dir={direction}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'linear-gradient(135deg, #304FFF 0%, #5a6fff 100%)',
                    color: 'white',
                    py: 1,
                }}
            >
                <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                    {t('LinkDocuments')}
                </Typography>
                <IconButton onClick={onClose} disabled={submitting} sx={{ color: 'white' }} aria-label={t('Close')}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                dir={direction}
                sx={{ textAlign: 'start', px: 3, py: 0.5, ...scrollbarSx }}
            >
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {groupMode && (
                    <Box sx={{ pb: 1 }}>
                        <SectionTitle>{t('LinkGroupReceiptTitle')}</SectionTitle>
                        {(!group || group.loading) && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress />
                            </Box>
                        )}
                        {group?.error && <Alert severity="error">{group.error}</Alert>}
                        {group && !group.loading && !group.error && (
                            <>
                                <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    {group.invoices.map((inv) => (
                                        <Chip
                                            key={inv.documentId}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            label={`${describe(inv)} · ${formatLinkAmount(inv.availableAmount)}`}
                                        />
                                    ))}
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Info label={t('LinkGroupReceiptTotal')} value={formatLinkAmount(group.total)} />
                                </Paper>
                                {group.closed.map((closedDoc) => (
                                    <Alert key={closedDoc.documentId} severity="warning" sx={{ mt: 1 }}>
                                        {t('LinkGroupReceiptInvoiceClosed', { document: describe(closedDoc) })}
                                    </Alert>
                                ))}
                                {group.closed.length === 0 && (
                                    <Alert severity="info" sx={{ mt: 1 }}>
                                        {t('LinkGroupReceiptHint', { amount: formatLinkAmount(group.total) })}
                                    </Alert>
                                )}
                                {group.closed.length === 0 && visibleGroupRows.length === 0 && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                        {t('LinkGroupReceiptNone', { amount: formatLinkAmount(group.total) })}
                                    </Alert>
                                )}
                                {hiddenGroupCount > 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {t('LinkGroupReceiptHidden', { count: hiddenGroupCount })}
                                    </Typography>
                                )}
                                {groupOwnInvoice && (
                                    <Box sx={{ mt: 1 }}>
                                        <Button size="small" variant="outlined" onClick={() => setGroupSkipped(true)} sx={{ textTransform: 'none' }}>
                                            {t('LinkGroupReceiptOnlyThis', { document: describe(groupOwnInvoice) })}
                                        </Button>
                                    </Box>
                                )}
                                {visibleGroupRows.length > 0 && (
                                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 360, ...scrollbarSx }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="center">{t('DocumentType')}</TableCell>
                                                    <TableCell align="center">{t('Reference')}</TableCell>
                                                    <TableCell align="center">{t('Date')}</TableCell>
                                                    <TableCell align="center">{t('Amount')}</TableCell>
                                                    <TableCell align="center">{t('LinkDocumentsAvailableAmount')}</TableCell>
                                                    <TableCell align="center">{t('Status')}</TableCell>
                                                    <TableCell align="center" />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {visibleGroupRows.map(({ candidate: c, items }) => {
                                                    const checking = items === 'loading';
                                                    return (
                                                        <TableRow
                                                            key={c.documentId}
                                                            hover={!checking}
                                                            onClick={checking ? undefined : () => setGroupReceiptId(String(c.documentId))}
                                                            sx={{ cursor: checking ? 'progress' : 'pointer' }}
                                                        >
                                                            <TableCell align="center">{typeLabel(c.documentType)}</TableCell>
                                                            <TableCell align="center">{c.reference}</TableCell>
                                                            <TableCell align="center">{formatLinkDate(c.documentDate)}</TableCell>
                                                            <TableCell align="center">{formatLinkAmount(c.amount)}</TableCell>
                                                            <TableCell align="center">{formatLinkAmount(c.availableAmount)}</TableCell>
                                                            <TableCell align="center">
                                                                <Chip
                                                                    size="small"
                                                                    variant={items === 'match' ? 'filled' : 'outlined'}
                                                                    color={items === 'match' ? 'success' : checking ? 'default' : 'info'}
                                                                    label={t(items === 'match'
                                                                        ? 'LinkGroupReceiptItemsMatch'
                                                                        : checking ? 'LinkGroupReceiptChecking' : 'LinkGroupReceiptAmountMatch')}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Button size="small" variant="contained" disabled={checking} sx={{ backgroundColor: '#304FFF' }}>
                                                                    {t('LinkGroupReceiptChoose')}
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </>
                        )}
                    </Box>
                )}

                {!groupMode && (groupReceiptId || groupSkipped) && invoiceGroupKey.includes(',') && !loading && (
                    <Box sx={{ pt: 1 }}>
                        <Button
                            size="small"
                            startIcon={direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                            onClick={() => { setGroupReceiptId(null); setGroupSkipped(false); }}
                            disabled={submitting}
                            sx={{ textTransform: 'none' }}
                        >
                            {t('LinkGroupReceiptBack')}
                        </Button>
                    </Box>
                )}

                {!loading && loadError && <Alert severity="error">{loadError}</Alert>}

                {!loading && !loadError && doc && (
                    <>
                        {/* A: selected / new document */}
                        <SectionTitle>{isCreate ? t('LinkDocumentsNewDocument') : t('LinkDocumentsSelectedDocument')}</SectionTitle>
                        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            <Info label={t('DocumentType')} value={typeLabel(doc.documentType)} />
                            {!isCreate && <Info label={t('Reference')} value={doc.reference} />}
                            <Info label={t('CustomerName')} value={doc.accountCodeName || draft?.customerName} />
                            {isCreate ? (
                                <Info label={t('Amount')} value={formatLinkAmount(doc.amount)} />
                            ) : (
                                <>
                                    <Info label={t('Date')} value={formatLinkDate(doc.documentDate)} />
                                    <Info label={t('LinkDocumentsOriginalAmount')} value={formatLinkAmount(doc.amount)} />
                                    <Info label={t('LinkDocumentsAppliedAmount')} value={formatLinkAmount(doc.appliedAmount)} />
                                    <Info label={t('LinkDocumentsRemainingAmount')} value={formatLinkAmount(doc.remainingAmount)} />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">{t('Status')}</Typography>
                                        {statusChip(t, doc)}
                                    </Box>
                                </>
                            )}
                        </Paper>
                        <Box sx={{ mt: 1, mb: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => toggleExpand(e, isCreate ? 'draft' : doc.documentId)}
                                startIcon={<ListAltOutlined sx={{ fontSize: 18 }} />}
                                endIcon={(
                                    <KeyboardArrowDown
                                        sx={{
                                            fontSize: 18,
                                            transition: 'transform 0.2s ease',
                                            transform: (isCreate ? expanded.draft : expanded[String(doc.documentId)]) ? 'rotate(180deg)' : 'none',
                                        }}
                                    />
                                )}
                                sx={itemsToggleSx(isCreate ? expanded.draft : expanded[String(doc.documentId)])}
                            >
                                {t((isCreate ? expanded.draft : expanded[String(doc.documentId)]) ? 'LinkDocumentsHideItems' : 'LinkDocumentsShowItems')}
                            </Button>
                            {(isCreate ? expanded.draft : expanded[String(doc.documentId)]) && (
                                <Box sx={{ mt: 0.5 }}>
                                    <LinkDocumentItemsPanel
                                        items={isCreate ? (draft?.items || []) : itemCache[String(doc.documentId)]?.items}
                                        loading={!isCreate && itemCache[String(doc.documentId)]?.loading}
                                        error={!isCreate ? itemCache[String(doc.documentId)]?.error : ''}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* B: existing chain (only for a document that already exists) */}
                        {!isCreate && <SectionTitle>{t('LinkDocumentsExistingChain')}</SectionTitle>}
                        {!isCreate && (options.chainLinks.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">{t('LinkDocumentsNoExistingLinks')}</Typography>
                        ) : (
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 180, ...scrollbarSx }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">{t('LinkDocumentsSource')}</TableCell>
                                            <TableCell align="center">{t('LinkDocumentsTarget')}</TableCell>
                                            <TableCell align="center">{t('LinkDocumentsRelation')}</TableCell>
                                            <TableCell align="center">{t('LinkDocumentsAppliedAmount')}</TableCell>
                                            <TableCell align="center" padding="checkbox" />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {options.chainLinks.map((l) => (
                                            <TableRow key={l.relationshipId}>
                                                <TableCell align="center">{describe(documentsById.get(String(l.sourceDocumentId)))}</TableCell>
                                                <TableCell align="center">{describe(documentsById.get(String(l.targetDocumentId)))}</TableCell>
                                                <TableCell align="center">
                                                    {t(l.relationType === RELATION_TYPE.PAYMENT_FOR ? 'LinkDocumentsRelationPayment' : 'LinkDocumentsRelationGenerated')}
                                                </TableCell>
                                                <TableCell align="center">{formatLinkAmount(l.appliedAmount)}</TableCell>
                                                <TableCell align="center" padding="checkbox">
                                                    <Tooltip title={t('LinkDocumentsRemoveLink')} arrow>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                disabled={submitting || removing}
                                                                onClick={() => setLinkToRemove(l)}
                                                                aria-label={t('LinkDocumentsRemoveLink')}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ))}

                        {!isCreate && pairNotLinkable && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                {pairKey.includes(',')
                                    ? t('LinkDocumentsSomeNotLinkable')
                                    : pairReason
                                        ? t('LinkDocumentsPairNotLinkableReason', { reason: pairReason })
                                        : t('LinkDocumentsPairNotLinkable')}
                            </Alert>
                        )}

                        {showCandidates ? (
                            <>
                                {/* C: candidates */}
                                <SectionTitle>{t('LinkDocumentsCandidates')}</SectionTitle>
                                <Box dir={direction} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                                    <TextField
                                        size="small"
                                        dir={direction}
                                        placeholder={t('LinkDocumentsSearchPlaceholder')}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="document-search-bar"
                                        variant="standard"
                                        style={{ '--xtram-search-max-width': '360px' }}
                                        InputProps={{
                                            disableUnderline: true,
                                            className: `custom-input-box-sizing ${inputAlignClass}`,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon className="document-search-icon" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    {anchors.length > 1 && (
                                        <Box sx={{ width: 360, flexShrink: 0, minWidth: 240 }}>
                                            <AutocompleteSelectFilter
                                                options={anchorOptions}
                                                selectedValues={anchorOptions.find((o) => o.value === selectedAnchorKey) || null}
                                                onChange={(event, newValue) => {
                                                    if (newValue?.value) handleAnchorChange(newValue.value);
                                                }}
                                                label={t('LinkDocumentsExtendFrom')}
                                                placeholder={t('LinkDocumentsExtendFrom')}
                                                isMultiple={false}
                                                direction={direction}
                                                portalMenu
                                            />
                                        </Box>
                                    )}
                                </Box>
                                {anchor && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {`${describe(anchorDocument)} — ${t(anchorRelationLabelKey(anchor))} · ${t('LinkDocumentsAvailableAmount')}: ${formatLinkAmount(anchor.availableAmount)} · ${t('LinkDocumentsLeftToLink')}: ${formatLinkAmount(remainingToLink)}`}
                                    </Typography>
                                )}
                                {selectionFull && selectedCandidates.length > 0 && (
                                    <Alert severity="info" sx={{ mb: 1 }}>
                                        {t('LinkDocumentsSelectionFull', { amount: formatLinkAmount(anchor.availableAmount) })}
                                    </Alert>
                                )}
                                {hiddenLargerCount > 0 && (
                                    <Alert severity="info" sx={{ mb: 1 }}>
                                        {t('LinkDocumentsPaymentHiddenLarger', {
                                            count: hiddenLargerCount,
                                            amount: formatLinkAmount(anchor?.availableAmount),
                                        })}
                                    </Alert>
                                )}
                                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, ...scrollbarSx }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox" />
                                                <TableCell padding="checkbox" />
                                                <TableCell align="center">{t('DocumentType')}</TableCell>
                                                <TableCell align="center">{t('Reference')}</TableCell>
                                                <TableCell align="center">{t('Date')}</TableCell>
                                                <TableCell align="center">{t('Amount')}</TableCell>
                                                <TableCell align="center">{t('LinkDocumentsAvailableAmount')}</TableCell>
                                                <TableCell align="center">{t('Status')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {visibleCandidates.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center">
                                                        <Typography variant="body2" color="text.secondary">{t('LinkDocumentsNoCandidates')}</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {visibleCandidates.map((c) => {
                                                const id = String(c.documentId);
                                                const selected = Object.prototype.hasOwnProperty.call(selections, id);
                                                const matchBlocked = isLinkCandidateBlocked(c, anchor?.relationType);
                                                const blocked = !selected
                                                    && (matchBlocked || selectionFull || isPaymentCandidateTooLarge(anchor, c, selections));
                                                const isOpen = !!expanded[id];
                                                const cache = itemCache[id] || {};
                                                const peerId = isCreate ? 'draft' : String(anchor?.documentId || options?.document?.documentId || '');
                                                const peerReady = isCreate || itemCache[peerId]?.loaded;
                                                const sourceId = Number(anchor?.direction) === LINK_DIRECTION.AS_TARGET
                                                    ? c.documentId
                                                    : (isCreate ? 'draft' : anchor?.documentId);
                                                const targetId = Number(anchor?.direction) === LINK_DIRECTION.AS_TARGET
                                                    ? (isCreate ? 'draft' : anchor?.documentId)
                                                    : c.documentId;
                                                const comparison = isOpen && cache.loaded && peerReady
                                                    ? compareItemRows(linesOf(sourceId), linesOf(targetId))
                                                    : [];
                                                return (
                                                    <React.Fragment key={id}>
                                                        <TableRow
                                                            hover={!blocked}
                                                            selected={selected}
                                                            onClick={() => toggleCandidate(c)}
                                                            sx={{ cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.5 : 1 }}
                                                        >
                                                            <TableCell padding="checkbox" onClick={(e) => toggleExpand(e, c.documentId)}>
                                                                <IconButton size="small" aria-label={t('LinkDocumentsShowItems')}>
                                                                    {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                                                </IconButton>
                                                            </TableCell>
                                                            <TableCell padding="checkbox">
                                                                <Checkbox size="small" checked={selected} disabled={blocked} />
                                                            </TableCell>
                                                            <TableCell align="center">{typeLabel(c.documentType)}</TableCell>
                                                            <TableCell align="center">{c.reference}</TableCell>
                                                            <TableCell align="center">{formatLinkDate(c.documentDate)}</TableCell>
                                                            <TableCell align="center">
                                                                <LinkDocumentMatchChip candidate={c} relationType={anchor?.relationType} />
                                                                {' '}
                                                                {formatLinkAmount(c.amount)}
                                                            </TableCell>
                                                            <TableCell align="center">{formatLinkAmount(c.availableAmount)}</TableCell>
                                                            <TableCell align="center">{statusChip(t, c)}</TableCell>
                                                        </TableRow>
                                                        {isOpen && (
                                                            <TableRow>
                                                                <TableCell colSpan={8} sx={{ backgroundColor: '#fafbff', py: 1 }}>
                                                                    <LinkDocumentItemsPanel
                                                                        items={cache.items}
                                                                        loading={cache.loading}
                                                                        error={cache.error}
                                                                    />
                                                                    {comparison.length > 0 && Number(anchor?.relationType) !== RELATION_TYPE.PAYMENT_FOR && (
                                                                        <Box sx={{ mt: 1, overflowX: 'auto' }}>
                                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#304FFF' }}>
                                                                                {t('LinkDocumentsItemComparison')}
                                                                            </Typography>
                                                                            <Table size="small">
                                                                                <TableHead>
                                                                                    <TableRow>
                                                                                        <TableCell align="center">{t('Makat')}</TableCell>
                                                                                        <TableCell align="center">{t('Name')}</TableCell>
                                                                                        <TableCell align="center">{t('LinkDocumentsSourceQty')}</TableCell>
                                                                                        <TableCell align="center">{t('LinkDocumentsTargetQty')}</TableCell>
                                                                                        <TableCell align="center">{t('LinkDocumentsItemRowResult')}</TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {comparison.map((row) => (
                                                                                        <TableRow key={row.key}>
                                                                                            <TableCell align="center">{row.makat || '—'}</TableCell>
                                                                                            <TableCell align="center">{row.name || row.label}</TableCell>
                                                                                            <TableCell align="center">{row.sourceQty}</TableCell>
                                                                                            <TableCell align="center">{row.targetQty}</TableCell>
                                                                                            <TableCell align="center">{t(comparisonResultKey(row.result))}</TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </Box>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {/* A payment is judged on all selected documents together (below), not per pair. */}
                                {!isPaymentLink && selectedCandidates.some((c) => Number(c.itemMatchStatus) === ITEM_MATCH_STATUS.AMOUNT_MISMATCH) && (
                                    <Alert severity="info" sx={{ mt: 1 }}>{t('LinkDocumentsAmountMismatchWarning')}</Alert>
                                )}
                                {!isPaymentLink && selectedCandidates.some((c) => Number(c.itemMatchStatus) === ITEM_MATCH_STATUS.AMOUNT_ONLY) && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>{t('LinkDocumentsAmountOnlyWarning')}</Alert>
                                )}
                                {!isPaymentLink && selectedCandidates.some((c) => Number(c.itemMatchStatus) === ITEM_MATCH_STATUS.MISMATCH) && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>{t('LinkDocumentsItemMismatchWarning')}</Alert>
                                )}
                                {selectedCandidates.some((c) => Number(c.itemMatchStatus) === ITEM_MATCH_STATUS.UNKNOWN)
                                    && Number(anchor?.relationType) !== RELATION_TYPE.PAYMENT_FOR && (
                                    <Alert severity="info" sx={{ mt: 1 }}>{t('LinkDocumentsItemUnknownWarning')}</Alert>
                                )}

                                {/* Proposed allocation */}
                                {selectedCandidates.length > 0 && (
                                    <>
                                        <SectionTitle>{t('LinkDocumentsProposedAllocation')}</SectionTitle>
                                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, ...scrollbarSx }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell align="center">{t('DocumentType')}</TableCell>
                                                        <TableCell align="center">{t('Reference')}</TableCell>
                                                        <TableCell align="center">{t('LinkDocumentsAvailableAmount')}</TableCell>
                                                        <TableCell align="center">{t('LinkDocumentsAmountToLink')}</TableCell>
                                                        {showRowStatus && (
                                                            <TableCell align="center">{t('LinkDocumentsResultingStatus')}</TableCell>
                                                        )}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {selectedCandidates.map((c) => {
                                                        const id = String(c.documentId);
                                                        const rowError = validation.rowErrors[id];
                                                        const rowProjection = rowError
                                                            ? null
                                                            : projectLink(selections[id], anchor, c, anchorDocument?.documentType);
                                                        const rowStatus = anchor?.direction === LINK_DIRECTION.AS_TARGET
                                                            ? rowProjection?.sourceStatus
                                                            : projection?.targetStatus;
                                                        return (
                                                            <TableRow key={id}>
                                                                <TableCell align="center">{typeLabel(c.documentType)}</TableCell>
                                                                <TableCell align="center">{c.reference}</TableCell>
                                                                <TableCell align="center">{formatLinkAmount(c.availableAmount)}</TableCell>
                                                                <TableCell align="center" sx={{ width: 220 }}>
                                                                    <TextField
                                                                        size="small"
                                                                        dir={direction}
                                                                        type="number"
                                                                        variant="standard"
                                                                        fullWidth
                                                                        value={selections[id]}
                                                                        onChange={(e) => setSelectionAmount(id, e.target.value)}
                                                                        disabled={isPaymentLink}
                                                                        error={!!rowError}
                                                                        helperText={rowError ? t(rowError) : ''}
                                                                        inputProps={{ min: 0, step: '0.01' }}
                                                                        InputProps={{
                                                                            disableUnderline: true,
                                                                            className: `custom-input-box-sizing ${inputAlignClass}`,
                                                                            sx: amountInputSx,
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                {showRowStatus && (
                                                                    <TableCell align="center">
                                                                        {rowProjection && rowStatus != null
                                                                            ? statusChip(t, { processingStatus: rowStatus, documentType: c.documentType })
                                                                            : '-'}
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <Paper variant="outlined" dir={direction} sx={{ mt: 1, p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
                                            <Info label={t('LinkDocumentsAvailableAmount')} value={formatLinkAmount(anchor?.availableAmount)} />
                                            <Info label={t('LinkDocumentsTotalToLink')} value={formatLinkAmount(validation.total)} />
                                            {projection && (
                                                <Info label={t('LinkDocumentsRemainingAfter')} value={formatLinkAmount(projection.anchorRemainingAfter)} />
                                            )}
                                            {projection && projection.anchorStatus != null && (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {t('LinkDocumentsResultingStatus')}
                                                    </Typography>
                                                    {statusChip(t, { processingStatus: projection.anchorStatus, documentType: anchorDocument?.documentType })}
                                                </Box>
                                            )}
                                            {validation.totalError && validation.totalError !== 'LinkDocumentsSelectCandidate' && (
                                                <Typography variant="body2" color="error" sx={{ alignSelf: 'center' }}>
                                                    {t(validation.totalError, validation.totalErrorParams || undefined)}
                                                </Typography>
                                            )}
                                        </Paper>
                                        {isPaymentLink && paymentItemsStatus === 'mismatch' && (
                                            <Alert severity="error" sx={{ mt: 1 }}>{t('LinkDocumentsPaymentItemsMismatch')}</Alert>
                                        )}
                                        {isPaymentLink && validation.isValid && !paymentItemsBlock && (
                                            <Alert severity="success" sx={{ mt: 1 }}>{t('LinkDocumentsPaymentMatched')}</Alert>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (!isCreate && !flowClosed ? (
                            <Alert severity="info" sx={{ mt: 2 }}>{t('LinkDocumentsNothingToLink')}</Alert>
                        ) : null)}

                        {submitError && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{submitError}</Alert>}
                    </>
                )}
            </DialogContent>

            <DialogActions dir={direction} sx={{ px: 3, py: 1.5, gap: 1 }}>
                <Button onClick={onClose} disabled={submitting} variant="outlined">
                    {t('Cancel')}
                </Button>
                {!flowClosed && (
                <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    variant="contained"
                    sx={{ backgroundColor: '#304FFF' }}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {isCreate ? t('Confirm') : t('LinkDocumentsSubmit')}
                </Button>
                )}
            </DialogActions>

            <Dialog
                open={!!linkToRemove}
                onClose={removing ? undefined : () => setLinkToRemove(null)}
                maxWidth="xs"
                fullWidth
                dir={direction}
                PaperProps={{ dir: direction }}
            >
                <DialogTitle dir={direction} sx={{ fontWeight: 700 }}>{t('LinkDocumentsRemoveLink')}</DialogTitle>
                <DialogContent dir={direction} sx={{ textAlign: 'start' }}>
                    <Typography variant="body2">
                        {linkToRemove && t('LinkDocumentsRemoveConfirm', {
                            source: describe(documentsById.get(String(linkToRemove.sourceDocumentId))),
                            target: describe(documentsById.get(String(linkToRemove.targetDocumentId))),
                            amount: formatLinkAmount(linkToRemove.appliedAmount),
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions dir={direction} sx={{ px: 3, py: 1.5, gap: 1 }}>
                    <Button onClick={() => setLinkToRemove(null)} disabled={removing} variant="outlined">
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={handleRemoveLink}
                        disabled={removing}
                        variant="contained"
                        color="error"
                        startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
                    >
                        {t('LinkDocumentsRemoveLinkConfirmButton')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default LinkDocumentsModal;
